import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  getPaymentTypesDropdownAction,
  getScanWeightType,
  getPaymentTypeDetailsAction,
  postTerminalChargesWholeRecordAction,
  getPaymentDetailsById,
  addPaymentReceivedDrawer,
  deleteCommercialPayement,
  voidCommercialPayement,
} from "./actions/commercialActions";

interface CommercialState {
  loader: true | false;
  paymentTypes: [];
  paymentTypeDetail: any;
  weightScanTypes: [];
  gatePassId: any;
  currentSelectedPaymentId: number;
  currentSelectedPaymentStatus: any;
  currentSelectedRegNo: number;
  amountDue: number;
  paymentDrawerDetails: [];
  currentContainerNo: string;
  currentVirNumber: string;
  currentIndexNumber: number;
}
const initialState: CommercialState = {
  loader: false,
  paymentTypes: [],
  paymentTypeDetail: [],
  weightScanTypes: [],
  gatePassId: "",
  currentSelectedPaymentId: 0,
  currentSelectedPaymentStatus: null,
  currentSelectedRegNo: 0,
  amountDue: 0,
  paymentDrawerDetails: [],
  currentContainerNo: "",
  currentVirNumber: "",
  currentIndexNumber: 0,
};

export const commercialSlice = createSlice({
  name: "commercial",
  initialState: initialState,
  reducers: {
    // handle Synchronous Calls
    removeGridItem: (state: any, record) => {
      const newData = state.weightScanTypes.filter((item: any) => item.id !== record.payload);
      return { ...state, weightScanTypes: newData };
    },
    addPaymentAction: (state: any, record: any) => {
      const oldRecord = [...state.weightScanTypes, record.payload];
      const newData = oldRecord.map((item: any, index: any) => {
        return {
          ...item,
          id: index + 1,
        };
      });
      return {
        ...state,
        weightScanTypes: newData,
      };
    },
    updatePaymentAction: (state: any, record: any) => {
      const { id, ...updates } = record.payload ?? {};
      const newData = state.weightScanTypes.map((item: any) =>
        item.id === id ? { ...item, ...updates } : item
      );
      return {
        ...state,
        weightScanTypes: newData,
      };
    },
    removeAllPayments: (state: any) => {
      return { ...state, weightScanTypes: [] };
    },
    setSelectedPaymentId: (state: CommercialState, action: any) => {
      state.currentSelectedPaymentId = action.payload.paymentId;
      state.currentSelectedPaymentStatus = action.payload.paymentStatus;
      state.currentSelectedRegNo = action.payload.regNo;
      state.currentContainerNo = action.payload.containerNo;
      state.currentVirNumber = action.payload.virNumber;
      state.currentIndexNumber = action.payload.indexNumber;
    },
    setAmountDue: (state: CommercialState, action: any) => {
      state.amountDue = action.payload;
    },
    clearPaymentTypeDetail: (state: CommercialState) => {
      state.paymentTypeDetail = [];
    },
  },
  extraReducers: (builder) => {
    // handle Asynchronous Calls

    // delete commercial payement methods
    builder.addCase(deleteCommercialPayement.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(deleteCommercialPayement.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(deleteCommercialPayement.fulfilled, (state) => {
      state.loader = false;
    });
    builder.addCase(voidCommercialPayement.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(voidCommercialPayement.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(voidCommercialPayement.fulfilled, (state) => {
      state.loader = false;
    });
    // Get getPaymentTypes methods
    builder.addCase(getPaymentTypesDropdownAction.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getPaymentTypesDropdownAction.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getPaymentTypesDropdownAction.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.paymentTypes = payload.map((item: any) => {
        return { ...item, isFixed: item.isFixed || 0 };
      });
    });
    // ./getPaymentTypes methods
    // Get getScanWeightType methods
    builder.addCase(getScanWeightType.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getScanWeightType.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getScanWeightType.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.gatePassId = payload.gatePassId;
      const newData = payload.weightScanningTypes.map((item: any, index: any) => {
        return {
          ...item,
          id: index + 1,
        };
      });
      state.weightScanTypes = newData;
    });
    // ./getScanWeightType methods

    // Get getPaymentTypeDetailsAction methods
    builder.addCase(getPaymentTypeDetailsAction.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getPaymentTypeDetailsAction.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getPaymentTypeDetailsAction.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.paymentTypeDetail = payload;
    });
    // ./getPaymentTypeDetailsAction methods
    // Get postTerminalChargesWholeRecordAction methods
    builder.addCase(postTerminalChargesWholeRecordAction.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(postTerminalChargesWholeRecordAction.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(postTerminalChargesWholeRecordAction.fulfilled, (state) => {
      state.loader = false;
    });
    // ./postTerminalChargesWholeRecordAction methods

    // Get getPaymentDetailsById methods
    builder.addCase(getPaymentDetailsById.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(getPaymentDetailsById.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(getPaymentDetailsById.fulfilled, (state, { payload }) => {
      state.loader = false;
      state.paymentDrawerDetails = payload;
    });
    // ./getPaymentDetailsById methods

    // Get addPaymentReceivedDrawer methods
    builder.addCase(addPaymentReceivedDrawer.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(addPaymentReceivedDrawer.rejected, (state) => {
      state.loader = false;
    });
    builder.addCase(addPaymentReceivedDrawer.fulfilled, (state) => {
      state.loader = false;
    });
    // ./addPaymentReceivedDrawer methods
  },
});

export const {
  addPaymentAction,
  updatePaymentAction,
  removeGridItem,
  removeAllPayments,
  setSelectedPaymentId,
  setAmountDue,
  clearPaymentTypeDetail,
} = commercialSlice.actions; // to Access Reducers methods

export const selectLoader = (state: RootState) => state.commercial.loader;
export const selectPaymentTypes = (state: RootState) => state.commercial.paymentTypes;
export const selectPaymentTypeDetail = (state: RootState) => state.commercial.paymentTypeDetail;
export const selectGatePassId = (state: RootState) => state.commercial.gatePassId;
export const selectWeightScanTypes = (state: RootState) => state.commercial.weightScanTypes;
export const currentSelectedPaymentId = (state: RootState) =>
  state.commercial.currentSelectedPaymentId;
export const currentSelectedPaymentStatus = (state: RootState) =>
  state.commercial.currentSelectedPaymentStatus;
export const currentSelectedRegNo = (state: RootState) => state.commercial.currentSelectedRegNo;
export const amountDue = (state: RootState) => state.commercial.amountDue;
export const selectPaymentDetails = (state: RootState) => state.commercial.paymentDrawerDetails;
export const currentContainerNo = (state: RootState) => state.commercial.currentContainerNo;
export const currentVirNumber = (state: RootState) => state.commercial.currentVirNumber;
export const currentIndexNumber = (state: RootState) => state.commercial.currentIndexNumber;
